import {
  BAR_ASSOCIATION_OPTIONS,
  CUSTOMER_STATUS_VALUES,
  LAST_CONTACT_BUCKET_OPTIONS,
  LEGAL_TRAINING_TERM_FILTER_OPTIONS,
  NEXT_ACTION_BUCKET_OPTIONS,
} from '@shared/constants/choices';
import {
  compactRecord,
  isPlainRecord,
  MAX_LIST_VIEW_SEARCH_LENGTH,
  readEnum,
  readIntegerInRange,
  readString,
  readStringArray,
  readUuidArray,
} from '@shared/lib/list-view-state';
import { ACTION_TYPE_ORDER } from '@shared/types/sales-action';
import type { LawyerListQuery } from '@shared/types/lawyer';

// sort_by は union を網羅した Record から導出する (メンバー追加で許可し忘れると typecheck が落ちる)。
const LAWYER_SORT_FIELDS = Object.keys({
  full_name: true,
  position: true,
  legal_training_term: true,
  firm_official_name: true,
  bar_association: true,
  status: true,
  next_action: true,
  next_action_due_at: true,
  last_contact_at: true,
  active_products: true,
  bar_admission_year: true,
  created_at: true,
  updated_at: true,
} satisfies Record<
  NonNullable<LawyerListQuery['sort_by']>,
  true
>) as NonNullable<LawyerListQuery['sort_by']>[];
const SORT_DIRECTIONS: NonNullable<LawyerListQuery['sort_dir']>[] = [
  'asc',
  'desc',
];
const LAST_CONTACT_BUCKETS = optionValues(
  LAST_CONTACT_BUCKET_OPTIONS,
) as NonNullable<NonNullable<LawyerListQuery['last_contact_buckets']>[number]>[];
const NEXT_ACTION_BUCKETS = optionValues(
  NEXT_ACTION_BUCKET_OPTIONS,
) as NonNullable<NonNullable<LawyerListQuery['next_action_buckets']>[number]>[];
const MIN_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 200;
// 弁護士会・修習期は静的カタログ由来。 カタログ外の保存値は破棄する (phantom フィルタ
// 防止)。 修習期は列フィルタが emit する擬似値 `__unset__` を含む合成候補を単一ソース
// から導出する。
const BAR_ASSOCIATIONS = optionValues(BAR_ASSOCIATION_OPTIONS);
const LEGAL_TRAINING_TERMS = optionValues(LEGAL_TRAINING_TERM_FILTER_OPTIONS);
const LAWYER_STATUS_VALUES = [...CUSTOMER_STATUS_VALUES] as NonNullable<
  LawyerListQuery['sales_statuses']
>;

export function createDefaultLawyerListQuery(): LawyerListQuery {
  // ステータスの既定は絞り込みなし (受注/失注を含む全9値表示)。
  return {
    sort_by: 'updated_at',
    sort_dir: 'desc',
    page: 1,
    page_size: 200,
  };
}

export function serializeLawyerListQuery(
  query: LawyerListQuery,
): Record<string, unknown> {
  return compactRecord({
    q: query.q?.slice(0, MAX_LIST_VIEW_SEARCH_LENGTH),
    positions: query.positions,
    legal_training_terms: query.legal_training_terms,
    bar_associations: query.bar_associations,
    product_ids: query.product_ids,
    assignee_account_ids: query.assignee_account_ids,
    sales_statuses: query.sales_statuses,
    last_contact_buckets: query.last_contact_buckets,
    next_action_buckets: query.next_action_buckets,
    next_action_types: query.next_action_types,
    sort_by: query.sort_by,
    sort_dir: query.sort_dir,
    page_size: query.page_size,
  });
}

export function validateLawyerListQuery(
  value: unknown,
): Partial<LawyerListQuery> {
  if (!isPlainRecord(value)) return {};

  const query: Partial<LawyerListQuery> = {
    q: readString(value.q, MAX_LIST_VIEW_SEARCH_LENGTH),
    positions: readStringArray(value.positions),
    legal_training_terms: readStringArray(
      value.legal_training_terms,
      LEGAL_TRAINING_TERMS,
    ),
    bar_associations: readStringArray(value.bar_associations, BAR_ASSOCIATIONS),
    product_ids: readUuidArray(value.product_ids),
    assignee_account_ids: readUuidArray(value.assignee_account_ids),
    last_contact_buckets: readStringArray(
      value.last_contact_buckets,
      LAST_CONTACT_BUCKETS,
    ),
    next_action_buckets: readStringArray(
      value.next_action_buckets,
      NEXT_ACTION_BUCKETS,
    ),
    next_action_types: readStringArray(
      value.next_action_types,
      ACTION_TYPE_ORDER,
    ),
    sort_by: readEnum(value.sort_by, LAWYER_SORT_FIELDS),
    sort_dir: readEnum(value.sort_dir, SORT_DIRECTIONS),
    page_size: readIntegerInRange(value.page_size, MIN_PAGE_SIZE, MAX_PAGE_SIZE),
  };

  if ('sales_statuses' in value) {
    // 全9値 (won/lost 含む) が許可対象。空・全選択は「絞り込みなし」として保存しない。
    // 旧版 (active 7値既定) の保存値は妥当な部分集合なので、そのまま明示フィルタとして
    // 復元される (チップから解除可能)。
    const statuses = readStringArray(value.sales_statuses, LAWYER_STATUS_VALUES);
    query.sales_statuses =
      statuses &&
      statuses.length > 0 &&
      statuses.length < LAWYER_STATUS_VALUES.length
        ? (statuses as NonNullable<LawyerListQuery['sales_statuses']>)
        : undefined;
  }

  return compactRecord(
    query as Record<string, unknown>,
  ) as Partial<LawyerListQuery>;
}

function optionValues(options: { value: string }[]): string[] {
  return options.map((option) => option.value);
}
